import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Stack,
    Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const KBD_SX = {
    px: 0.85,
    py: 0.15,
    minWidth: 22,
    textAlign: 'center',
    fontFamily: 'monospace',
    fontSize: 12,
    fontWeight: 700,
    border: (t) => `1px solid ${t.palette.divider}`,
    borderRadius: 1,
    bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.800' : 'grey.100'),
    color: 'text.primary',
};

const Kbd = ({ children }) => (
    <Box component="span" sx={KBD_SX}>{children}</Box>
);

const Row = ({ keys, label }) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Typography variant="body2">{label}</Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
            {keys.map((k, i) => (
                <React.Fragment key={i}>
                    {i > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mx: 0.25 }}>
                            +
                        </Typography>
                    )}
                    <Kbd>{k}</Kbd>
                </React.Fragment>
            ))}
        </Stack>
    </Stack>
);

const ShortcutsHelpDialog = ({ open, onClose }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const navRows = [
        { keys: ['g', 'd'], label: isArabic ? 'الرئيسية' : 'Dashboard' },
        { keys: ['g', 'o'], label: isArabic ? 'الطلبات'  : 'Orders' },
        { keys: ['g', 'c'], label: isArabic ? 'العملاء'  : 'Customers' },
        { keys: ['g', 'p'], label: isArabic ? 'المنتجات' : 'Products' },
        { keys: ['g', 'i'], label: isArabic ? 'المخزون'  : 'Inventory' },
        { keys: ['g', 's'], label: isArabic ? 'الإعدادات' : 'Settings' },
    ];

    const generalRows = [
        { keys: ['Ctrl', 'K'], label: isArabic ? 'فتح البحث الشامل' : 'Open command palette' },
        { keys: ['/'], label: isArabic ? 'تركيز شريط البحث' : 'Focus the search bar' },
        { keys: ['?'], label: isArabic ? 'عرض هذه القائمة' : 'Show this list' },
    ];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isArabic ? 'اختصارات لوحة المفاتيح' : 'Keyboard shortcuts'}
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {isArabic ? 'التنقل' : 'Navigation'}
                </Typography>
                <Stack spacing={1.25}>
                    {navRows.map((r, i) => <Row key={i} {...r} />)}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {isArabic ? 'عام' : 'General'}
                </Typography>
                <Stack spacing={1.25}>
                    {generalRows.map((r, i) => <Row key={i} {...r} />)}
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                    {isArabic
                        ? 'اضغط الحرف الأول ثم الثاني خلال أقل من ثانية لتنفيذ الاختصار.'
                        : 'Press the first key then the second within a second to fire the chord.'}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button variant="contained" onClick={onClose}>
                    {isArabic ? 'إغلاق' : 'Close'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ShortcutsHelpDialog;
